// ============================================================
// systems/competitive.js — Competitive mode: lives, shields, damage
// ============================================================
import { state } from '../state.js';
import { dom } from '../dom.js';
import { playLifeLostSound, playShieldPickupSound } from '../audio/sfx.js';
import { updateShopUI } from './shop.js';
import { isLoggedIn, showAuthModal } from './auth.js';
import { supabase } from './supabase.js';

// ---- Lives ----

export function loseLife() {
  if (state.gameMode !== 'competitive') return false;
  state.lives--;
  // Animate the lost heart
  const heart = document.getElementById('life-' + (state.lives + 1));
  if (heart) {
    heart.classList.add('hit');
    setTimeout(() => heart.classList.add('lost'), 300);
  }

  // Screen shake
  document.body.style.boxShadow = 'inset 0 0 100px rgba(255,0,0,0.5)';
  setTimeout(() => document.body.style.boxShadow = '', 300);

  // Life lost sound
  playLifeLostSound();

  if (state.lives <= 0) {
    // Game over — save high score
    if (state.score > state.highScore) {
      state.highScore = state.score;
      localStorage.setItem('smoothClicker_highScore', state.highScore.toString());
      dom.highScoreVal.textContent = state.highScore;

      // Sync high score to leaderboard if logged in
      if (state.onlineMode && state.supabaseUser) {
        const meta = state.supabaseUser.user_metadata || {};
        const emailPrefix = state.supabaseUser.email ? state.supabaseUser.email.split('@')[0] : 'Player';
        const displayName =
          state.userProfile?.display_name ||
          state.userProfile?.username ||
          meta.display_name || meta.full_name || meta.name || meta.user_name ||
          emailPrefix; // never the raw email
        supabase.from('leaderboard').upsert({
          id: state.supabaseUser.id,
          display_name: displayName,
          high_score: state.highScore,
          updated_at: new Date().toISOString(),
        }).then(({ error }) => {
          if (error) console.warn('[competitive] leaderboard upsert error:', error.message);
        }, (err) => console.warn('[competitive] leaderboard upsert threw:', err?.message || err));
      }
    }
    // Trigger death screen — build the DOM instead of innerHTML concat to avoid
    // any chance of HTML injection through state.score (which is always numeric,
    // but keeping the boundary clean is cheap insurance).
    state.dead = true;
    dom.finalScoreEl.textContent = state.score;
    const p = dom.gameOverEl.querySelector('p');
    p.textContent = '';
    p.append('You ran out of lives! Final score: ');
    const scoreSpan = document.createElement('span');
    scoreSpan.id = 'final-score';
    scoreSpan.textContent = state.score;
    p.append(scoreSpan);
    if (state.score >= state.highScore) {
      p.append(document.createElement('br'));
      p.append('NEW HIGH SCORE!');
    }
    dom.gameOverEl.classList.add('show');
    dom.smiley.style.pointerEvents = 'none';

    // Prompt to create account if not logged in
    if (!isLoggedIn() && state.score > 0) {
      setTimeout(() => {
        showAuthModal('Sign in to save your high score to the leaderboard!');
      }, 1500);
    }

    return true; // dead
  }
  return false;
}

export function resetLives() {
  state.lives = 3;
  for (let i = 1; i <= 3; i++) {
    const h = document.getElementById('life-' + i);
    if (h) { h.classList.remove('lost', 'hit'); }
  }
}

// ---- Shields ----

export function startShieldSpawning() {
  if (state.shieldInterval) return;
  // Spawn a shield rarely — every 20-40 seconds
  state.shieldInterval = setInterval(() => {
    if (state.gameMode !== 'competitive' || state.dead || state.paused) return;
    if (state.lives >= 3) return; // Don't spawn if full health
    if (state.activeShields.length >= 1) return; // Max 1 on screen
    spawnShield();
  }, 20000 + Math.random() * 20000);
}

export function stopShieldSpawning() {
  clearInterval(state.shieldInterval);
  state.shieldInterval = null;
  state.activeShields.forEach(s => s.remove());
  state.activeShields = [];
}

export function spawnShield() {
  const shield = document.createElement('div');
  shield.className = 'shield-pickup';
  shield.textContent = '\uD83D\uDEE1';

  // Place on the plushie's head
  const rect = dom.smiley.getBoundingClientRect();
  const x = rect.left + rect.width / 2 - 14;
  const y = rect.top - 20;
  shield.style.left = x + 'px';
  shield.style.top = y + 'px';

  // Show "Click me!" hint the first time
  let hint = null;
  if (state.shieldFirstTime) {
    hint = document.createElement('div');
    hint.className = 'shield-hint';
    hint.textContent = 'Click me!';
    hint.style.left = (x + 30) + 'px';
    hint.style.top = (y + 2) + 'px';
    document.body.appendChild(hint);
  }

  shield.addEventListener('click', (e) => {
    e.stopPropagation();
    if (state.lives >= 3) return;

    // Heal 1 heart
    state.lives++;
    const heart = document.getElementById('life-' + state.lives);
    if (heart) {
      heart.classList.remove('lost', 'hit');
      heart.style.animation = 'none';
      heart.offsetHeight;
      heart.style.animation = '';
    }

    state.shieldFirstTime = false;

    // Pickup sound
    playShieldPickupSound();

    // Flash green
    document.body.style.boxShadow = 'inset 0 0 80px rgba(0,255,100,0.3)';
    setTimeout(() => document.body.style.boxShadow = '', 300);

    // Remove shield and hint
    shield.remove();
    if (hint) hint.remove();
    state.activeShields = state.activeShields.filter(s => s !== shield);
  });

  document.body.appendChild(shield);
  state.activeShields.push(shield);

  // Disappears after 5 seconds
  setTimeout(() => {
    if (document.body.contains(shield)) {
      shield.remove();
      if (hint && document.body.contains(hint)) hint.remove();
      state.activeShields = state.activeShields.filter(s => s !== shield);
    }
  }, 5000);
}

export function showLivesUI(show) {
  dom.livesDisplay.classList.toggle('show', show);
  dom.highScoreDisplay.classList.toggle('show', show);
}

// ---- Central damage handler ----

/**
 * Handle hazard damage — in competitive mode lose a life,
 * in normal mode subtract clicks from score.
 * Returns true if the player died (competitive only).
 */
export function applyHazardDamage(amount) {
  if (state.gameMode === 'competitive') {
    return loseLife();
  } else {
    state.score = Math.max(0, state.score - amount);
    dom.scoreEl.textContent = state.score;
    updateShopUI();
    return false;
  }
}
